import api from '../config/axiosConfig';
import { Mutation } from '../types/mutation';
import { API_BASE_URL } from '../utils/constants';

export enum MutationStatus {
  PENDING = 'PENDING',
  CONFIRMED = 'CONFIRMED',
  CANCELLED = 'CANCELLED',
}

export interface MutationItem {
  id: number;
  item_id: number;
  item_sku: string;
  item_name: string;
  qty: number;
}

export interface MutationResponse extends Mutation {
  mutation_no: string;
  from_branch_id: number;
  to_branch_id: number;
  status: MutationStatus;
  notes?: string;
  created_by: number;
  confirmed_by?: number;
  confirmed_at?: string;
  created_at: string;
  items: MutationItem[];
}

export interface MutationCreate {
  item_id: number;
  to_branch_id: number;
  qty: number;
  notes?: string;
}

export interface MutationConfirm {
  notes?: string;
}

const API_URL = `${API_BASE_URL}/mutations`;

// Fetch all mutations
export const fetchMutations = async (params?: {
  from_branch_id?: number;
  to_branch_id?: number;
  status?: MutationStatus;
  page?: number;
  page_size?: number;
}): Promise<MutationResponse[]> => {
  const response = await api.get<MutationResponse[]>(API_URL, { params });
  return response.data;
};

export const getMutationById = async (id: number): Promise<MutationResponse> => {
  const response = await api.get<MutationResponse>(`${API_URL}/${id}`);
  return response.data;
};

// Create a new mutation
export const createMutation = async (
  mutationData: MutationCreate
): Promise<MutationResponse> => {
  const response = await api.post<MutationResponse>(API_URL, mutationData);
  return response.data;
};

// Approve/Confirm a mutation (backend uses /confirm not /approve)
export const approveMutation = async (
  mutationId: number | string,
  data?: MutationConfirm
): Promise<MutationResponse> => {
  const id = typeof mutationId === 'string' ? parseInt(mutationId) : mutationId;
  const response = await api.post<MutationResponse>(
    `${API_URL}/${id}/confirm`,
    data
  );
  return response.data;
};

export const confirmMutation = async (
  mutationId: number,
  data?: MutationConfirm
): Promise<MutationResponse> => {
  const response = await api.post<MutationResponse>(
    `${API_URL}/${mutationId}/confirm`,
    data
  );
  return response.data;
};

// Cancel a mutation
export const cancelMutation = async (
  mutationId: number,
  reason: string
): Promise<MutationResponse> => {
  const response = await api.post<MutationResponse>(
    `${API_URL}/${mutationId}/cancel`,
    { reason }
  );
  return response.data;
};

// Legacy function names for backward compatibility
export const updateMutation = async (mutationId: string, mutationData: any) => {
  console.warn('updateMutation is deprecated, use confirmMutation instead');
  return confirmMutation(parseInt(mutationId), mutationData);
};

export const deleteMutation = async (mutationId: string) => {
  console.warn('deleteMutation is deprecated, use cancelMutation instead');
  return cancelMutation(parseInt(mutationId), 'Deleted by user');
};
